import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { Button } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { ArrowLeftIcon } from "lucide-react";
import { useState } from "react";
import type { BulkCreateRoomBookingsDTO } from "../../domain/contracts/bulk-room-bookings";
import { useBulkCreateRoomBookings } from "../api/queries";
import { buildRoomBookingSuccessSearch } from "../lib/room-booking-success-search";
import { RoomBookingBulkCreateForm } from "../ui/RoomBookingBulkCreateForm";

export function RoomBookingBulkCreatePage() {
  const nav = useNavigate({ from: "/app/room-bookings/bulk-create" });
  const search = useSearch({ from: "/app/room-bookings/bulk-create" });
  const bulkCreate = useBulkCreateRoomBookings();
  const [conflicts, setConflicts] = useState<
    Array<{
      sessionIndex: number;
      date: string;
      startTime: string;
      endTime: string;
      message: string;
    }>
  >([]);

  const handleSubmit = async (values: BulkCreateRoomBookingsDTO) => {
    try {
      const result = await bulkCreate.mutateAsync(values);
      if (result.conflicts.length > 0) {
        setConflicts(result.conflicts);
        return;
      }
      setConflicts([]);

      const firstBookingId = result.bookings[0]?.id;
      if (firstBookingId) {
        const successSearch = buildRoomBookingSuccessSearch(firstBookingId, {
          receiptId: result.receiptId,
          credit: result.credit,
        });
        if (successSearch) {
          nav({
            to: "/app/room-bookings/success",
            search: successSearch,
          });
          return;
        }
      }

      nav({ to: "/app/room-bookings" });
    } catch {
      // Mutation surfaces toast
    }
  };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ການຈອງຫ້ອງ", to: "/app/room-bookings" },
            { label: "ຈອງຫຼາຍຄັ້ງ" },
          ]}
        />
      </Header>

      <Main>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="font-bold text-2xl tracking-tight">
              ຈອງຫ້ອງຫຼາຍຄັ້ງ
            </h2>
            <p className="text-muted-foreground">
              ເລືອກຊ່ວງວັນທີ ແລະ ກຳນົດວັນໃນອາທິດ + ເວລາ — ຮູບແບບດຽວກັບສ້າງຫຼາຍຊົ່ວໂມງຮຽນ
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => nav({ to: "/app/room-bookings/new" })}
            >
              ຈອງຄັ້ງດຽວ
            </Button>
            <Button
              variant="outline"
              onClick={() => nav({ to: "/app/room-bookings" })}
            >
              <ArrowLeftIcon className="size-4" />
              ກັບຄືນ
            </Button>
          </div>
        </div>

        <div className="mt-6">
          <RoomBookingBulkCreateForm
            initialDate={
              typeof search.date === "string" ? search.date : undefined
            }
            initialStartTime={
              typeof search.startTime === "string"
                ? search.startTime
                : undefined
            }
            initialEndTime={
              typeof search.endTime === "string" ? search.endTime : undefined
            }
            onSubmit={handleSubmit}
            submitting={bulkCreate.isPending}
            conflicts={conflicts}
          />
        </div>
      </Main>
    </>
  );
}
